import { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags.js";
import { bulkReferenceRuns, externalReferences } from "../../../api/addressBook.service.js";
import { countsFor, shouldCheckpoint, toRunRows } from "./reportRun.js";
import { parseCsv, type ParseResult } from "./parseCsv.js";
import { companiesToCheck, planUpload, type UploadPlan } from "./planUpload.js";
import { assertStillUnmapped, checkExisting } from "./checkExisting.js";
import { outcomeGroup, runUpload, type RowResult } from "./runUpload.js";
import { downloadResultsCsv } from "./resultsExport.js";
import { ResultsTable, type ResultsFilter } from "./ResultsTable.js";
import { PreviousRuns } from "./PreviousRuns.js";
import styles from "./BulkExternalRefs.module.css";

/**
 * Bulk-attaches external references to address-book companies from a CSV.
 *
 * Deliberately has no backend of its own: it loops the existing per-row endpoint
 * through staff-bff, which already proxies address-book-api and enforces
 * `addressbook.write`. The write is idempotent, so an interrupted run is resolved by
 * uploading the same file again rather than by tracking progress server-side.
 */

/** Offered reference types. The API takes a free-form string; these are the ones we support. */
const REFERENCE_TYPES = ["mondaq_id"] as const;

type Phase = "idle" | "checking" | "previewed" | "running" | "done";

/** Gates the page itself, not just the nav entry — the route is registered unconditionally. */
export const BULK_EXTERNAL_REFS_FLAG = "bulk-external-references";

export default function BulkExternalRefs() {
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [referenceType, setReferenceType] = useState<string>(REFERENCE_TYPES[0]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [plan, setPlan] = useState<UploadPlan | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<RowResult[]>([]);
  const [filter, setFilter] = useState<ResultsFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  /** Set while a run is being recorded; kept so a mid-run failure is still traceable. */
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [savedRunId, setSavedRunId] = useState<string | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);

  // Read and write are deliberately separate. Performing an upload is a developer
  // action; reviewing what one did is not, and the people who need to read a run are
  // exactly the roles that hold read without write (product, research managers).
  const canRead = hasPermission("addressbook.read");
  const canWrite = hasPermission("addressbook.write");

  const reset = useCallback(() => {
    setFileName(null);
    setActiveRunId(null);
    setSavedRunId(null);
    setSaveWarning(null);
    setPlan(null);
    setResults([]);
    setFilter("all");
    setError(null);
    setProgress({ done: 0, total: 0 });
    setPhase("idle");
  }, []);

  const readReferences = useCallback(async (companyUuid: string) => {
    const response = await externalReferences.getByEntity("company", companyUuid);
    return response.data;
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      reset();
      setFileName(file.name);

      let parseResult: ParseResult;
      try {
        parseResult = parseCsv(await file.text(), { referenceType });
      } catch (parseError) {
        setError(parseError instanceof Error ? parseError.message : String(parseError));
        return;
      }
      if (parseResult.rows.length === 0) {
        setError("That file has a header but no rows.");
        return;
      }

      // Read what each company already holds, so anyone already mapped is skipped
      // rather than given a second reference as an alias.
      setPhase("checking");
      setProgress({ done: 0, total: companiesToCheck(parseResult.rows).length });
      try {
        const existing = await checkExisting(
          companiesToCheck(parseResult.rows),
          referenceType,
          readReferences,
          { onProgress: (done, total) => setProgress({ done, total }) },
        );
        setPlan(planUpload(parseResult.rows, existing));
        setPhase("previewed");
      } catch (checkError) {
        setError(checkError instanceof Error ? checkError.message : String(checkError));
        setPhase("idle");
      }
    },
    [readReferences, referenceType, reset],
  );

  const write = useCallback(
    async (companyUuid: string, referenceValue: string) => {
      // Re-read immediately before writing. The plan is a snapshot taken at preview
      // time, and the API would happily store a differing value as a second alias, so
      // without this a reference added in between would defeat the one-per-company rule.
      await assertStillUnmapped(companyUuid, referenceType, readReferences);
      await externalReferences.create("company", companyUuid, {
        reference_type: referenceType,
        reference_value: referenceValue,
      });
    },
    [readReferences, referenceType],
  );

  const startRun = useCallback(async () => {
    if (!plan) {
      return;
    }
    setPhase("running");
    setProgress({ done: 0, total: plan.rows.length });

    // Open a run record first, so an upload that dies mid-way still leaves something
    // visible. Recording is best-effort: a reporting failure must never stop the actual
    // work, so every call here is caught and ignored.
    let runId: string | null = null;
    try {
      const opened = await bulkReferenceRuns.open({
        reference_type: referenceType,
        file_name: fileName,
        total_rows: plan.rows.length,
      });
      runId = opened.data.id;
      setActiveRunId(runId);
    } catch {
      // Reporting is not the job. Carry on without a run record.
    }

    const checkpoint = (settled: RowResult[]) => {
      if (!runId) {
        return;
      }
      void bulkReferenceRuns
        .report(runId, { ...countsFor(settled), rows: toRunRows(settled) })
        .catch(() => {
          // A dropped checkpoint costs at most the rows since the last one.
        });
    };

    try {
      const runResults = await runUpload(plan.rows, write, {
        onProgress: (done, total, settled) => {
          setProgress({ done, total });
          if (shouldCheckpoint(done, total)) {
            checkpoint(settled);
          }
        },
      });
      setResults(runResults);
      setPhase("done");

      if (runId) {
        try {
          await bulkReferenceRuns.report(runId, {
            status: "completed",
            ...countsFor(runResults),
            rows: toRunRows(runResults),
          });
          setSavedRunId(runId);
        } catch {
          setSaveWarning(
            "The run finished, but saving it for later failed. Export the CSV if you need a copy.",
          );
        }
      }
    } catch (runError) {
      // runUpload absorbs per-row API failures, so reaching here means something
      // unexpected broke. Surface it rather than leaving the page stuck on a progress bar.
      setError(runError instanceof Error ? runError.message : String(runError));
      setPhase("previewed");
    }
  }, [fileName, plan, referenceType, write]);

  const retryFailed = useCallback(async () => {
    // Only outcomes another attempt could change. A 404 or a 400 will simply repeat
    // itself, so retrying them wastes requests and muddies the result.
    const retryable = results.filter(
      result => result.outcome === "failed" || result.outcome === "writes-disabled",
    );
    if (retryable.length === 0) {
      return;
    }
    setPhase("running");
    setProgress({ done: 0, total: retryable.length });

    try {
      // Forced back to "write" — these rows were eligible, the attempt is what failed.
      const retried = await runUpload(
        retryable.map(result => ({ ...result.row, plan: "write" as const })),
        write,
        { onProgress: (done, total) => setProgress({ done, total }) },
      );

      const byKey = new Map(
        retried.map(result => [`${result.row.lineNumber}-${result.row.companyUuid}`, result]),
      );
      // Merged explicitly rather than inside a setState callback, so the same value can
      // be both shown and saved — otherwise the two drift.
      const merged = results.map(
        result => byKey.get(`${result.row.lineNumber}-${result.row.companyUuid}`) ?? result,
      );
      setResults(merged);

      // Re-report, or the saved run keeps the outcomes the retry just corrected — leaving
      // whoever opens it later looking at failures that no longer exist.
      if (activeRunId) {
        try {
          await bulkReferenceRuns.report(activeRunId, {
            status: "completed",
            ...countsFor(merged),
            rows: toRunRows(merged),
          });
          setSaveWarning(null);
        } catch {
          setSaveWarning(
            "The retry finished, but updating the saved run failed. What you see here is correct; the saved copy is out of date.",
          );
        }
      }
    } catch (retryError) {
      setError(retryError instanceof Error ? retryError.message : String(retryError));
    }
    setPhase("done");
  }, [activeRunId, results, write]);

  const exportResults = useCallback(() => {
    downloadResultsCsv(results, referenceType, new Date());
  }, [results, referenceType]);

  const resultCounts = useMemo(() => {
    const tally = { created: 0, skipped: 0, failed: 0 };
    for (const result of results) {
      tally[outcomeGroup(result.outcome)] += 1;
    }
    return tally;
  }, [results]);

  // Gate here rather than only on the nav entry: the route is registered
  // unconditionally, so hiding the link alone would leave the page reachable by URL.
  if (!hasFlag(BULK_EXTERNAL_REFS_FLAG)) {
    return (
      <div className={styles.page}>
        <UnauthorizedView message="Bulk external reference uploads are not enabled. An administrator can switch the Bulk External References flag on under Developer · Feature Flags." />
      </div>
    );
  }

  // Either permission gets you in. Write is checked as well as read so that granting
  // write alone still reaches the upload — every role today carries both, but gating the
  // page on read would otherwise lock out a write-only grant that used to work.
  if (!canRead && !canWrite) {
    return (
      <div className={styles.page}>
        <UnauthorizedView message="You need the addressbook.read permission to view external reference uploads." />
      </div>
    );
  }

  // Read without write is a first-class state, not a degraded one: it is how someone
  // who did not perform an upload reviews it. They get the history and nothing else —
  // no file picker, no reference type, no confirm.
  if (!canWrite) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Bulk External References"
          subtitle="Review past bulk uploads of external reference ids"
        />
        <div className={styles.card}>
          <PreviousRuns />
        </div>
      </div>
    );
  }

  const isBusy = phase === "checking" || phase === "running";
  const percent = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Bulk External References"
        subtitle="Attach external reference ids to address-book companies from a CSV"
      />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Choose file</div>
        <div
          className={`${styles.drop} ${isDragging ? styles.dropActive : ""}`}
          onDragOver={event => {
            // Still prevented while busy, or the browser navigates away to the dropped
            // file. Only the drop-active styling is suppressed.
            event.preventDefault();
            if (!isBusy) {
              setIsDragging(true);
            }
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={event => {
            event.preventDefault();
            setIsDragging(false);
            // Loading a second file mid-run would reset() out from under the chain that
            // is still writing: its results land afterwards, over the new file's state,
            // and its checkpoints keep reporting against a run the page has forgotten.
            if (isBusy) {
              return;
            }
            const dropped = event.dataTransfer.files[0];
            if (dropped) {
              void loadFile(dropped);
            }
          }}
        >
          <div className={styles.dropTitle}>{fileName ?? "Drop a CSV here, or browse"}</div>
          <div className={styles.dropHint}>
            Two columns: <code>company_uuid,reference_value</code>. Tab-separated files and extra
            columns are fine. Nothing is written until you confirm.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            aria-label="CSV of company and reference value pairs"
            accept=".csv,.tsv,.txt,text/csv"
            className={styles.hiddenInput}
            // Visually hidden, not display:none, so it stays reachable by keyboard and
            // assistive tech — which means it needs the same disabled state as Browse.
            disabled={isBusy}
            onChange={event => {
              const chosen = event.target.files?.[0];
              // Clear the input's value, otherwise picking the SAME file again fires no
              // change event and appears to do nothing — which is exactly what someone
              // re-uploading to confirm the run was idempotent would try.
              event.target.value = "";
              if (chosen && !isBusy) {
                void loadFile(chosen);
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy}
          >
            Browse
          </Button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>2 · Reference type</div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="reference-type">
            Write these values as
          </label>
          <select
            id="reference-type"
            className={styles.select}
            value={referenceType}
            disabled={isBusy || phase === "previewed"}
            onChange={event => {
              setReferenceType(event.target.value);
              reset();
            }}
          >
            {REFERENCE_TYPES.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {phase === "checking" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Checking existing references</div>
          <div className={styles.progressRow}>
            <span>
              {progress.done} of {progress.total} companies checked
            </span>
            <strong>{percent}%</strong>
          </div>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${percent}%` }} />
          </div>
        </div>
      ) : null}

      {plan && phase === "previewed" ? (
        <>
          <div className={styles.card}>
            <div className={styles.cardTitle}>Preview</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileCreated}`}>
                <div className={styles.tileNumber}>{plan.counts.write}</div>
                <div className={styles.tileLabel}>Will be written</div>
              </div>
              <div className={`${styles.tile} ${styles.tileSkipped}`}>
                <div className={styles.tileNumber}>
                  {plan.counts.alreadyMapped + plan.counts.duplicateInFile}
                </div>
                <div className={styles.tileLabel}>Already mapped — skip</div>
              </div>
              <div className={`${styles.tile} ${styles.tileFailed}`}>
                <div className={styles.tileNumber}>{plan.counts.malformed}</div>
                <div className={styles.tileLabel}>Invalid rows</div>
              </div>
            </div>
          </div>

          <div className={`${styles.banner} ${styles.bannerInfo}`}>
            One reference per company. A company that already holds a {referenceType} is skipped
            rather than given a second — so this fills blanks and never corrects. Changing a stored
            value means removing the old one first.
          </div>

          {plan.repeatedCompanies.length > 0 ? (
            <div className={`${styles.banner} ${styles.bannerWarning}`}>
              {plan.repeatedCompanies.length} compan
              {plan.repeatedCompanies.length === 1 ? "y appears" : "ies appear"} more than once in
              this file — only the first row of each is written:{" "}
              {plan.repeatedCompanies
                .map(company =>
                  company.rows.map(row => row.label || row.referenceValue).join(" + "),
                )
                .join("; ")}
              .
            </div>
          ) : null}

          <div className={styles.toolbar}>
            <span className={styles.spacer} />
            <Button variant="outlined" onClick={reset}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void startRun()}
              disabled={plan.counts.write === 0}
            >
              Write {plan.counts.write} reference{plan.counts.write === 1 ? "" : "s"}
            </Button>
          </div>
        </>
      ) : null}

      {phase === "running" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Writing</div>
          <div className={styles.progressRow}>
            <span>
              {progress.done} of {progress.total} rows processed
            </span>
            <strong>{percent}%</strong>
          </div>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${percent}%` }} />
          </div>
          {activeRunId ? (
            <div className={styles.rowNote}>
              Progress is being saved every 100 rows, so closing this tab will not lose what has
              already run.
            </div>
          ) : null}
        </div>
      ) : null}

      {phase === "done" ? (
        <>
          {savedRunId ? (
            <div className={`${styles.banner} ${styles.bannerInfo}`}>
              Saved. Anyone with address-book access can open this run later from Previous runs — no
              need to send a file.
            </div>
          ) : null}

          {saveWarning ? (
            <div className={`${styles.banner} ${styles.bannerWarning}`} role="alert">
              {saveWarning}
            </div>
          ) : null}

          {results.some(result => result.outcome === "writes-disabled") ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              Writes are disabled, so the run stopped. Turn on the Address Book Live flag under
              Developer · Feature Flags, then upload the file again — nothing was written.
            </div>
          ) : null}

          <div className={styles.card}>
            <div className={styles.cardTitle}>Result</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileCreated}`}>
                <div className={styles.tileNumber}>{resultCounts.created}</div>
                <div className={styles.tileLabel}>Created</div>
              </div>
              <div className={`${styles.tile} ${styles.tileSkipped}`}>
                <div className={styles.tileNumber}>{resultCounts.skipped}</div>
                <div className={styles.tileLabel}>Skipped</div>
              </div>
              <div className={`${styles.tile} ${styles.tileFailed}`}>
                <div className={styles.tileNumber}>{resultCounts.failed}</div>
                <div className={styles.tileLabel}>Failed</div>
              </div>
            </div>
          </div>

          <ResultsTable
            results={results}
            filter={filter}
            onFilterChange={setFilter}
            onExport={exportResults}
            onRetryFailed={() => void retryFailed()}
            isBusy={isBusy}
            referenceType={referenceType}
          />

          <div className={styles.toolbar}>
            <Button variant="outlined" onClick={reset}>
              Upload another file
            </Button>
          </div>
        </>
      ) : null}

      {/* Only while idle — during a run the live results are what matters, and showing
          both a live and a historical table at once invites reading the wrong one. */}
      {phase === "idle" ? (
        <div className={styles.card}>
          <PreviousRuns />
        </div>
      ) : null}
    </div>
  );
}
